'use client';

import { useState } from 'react';
import { useOptionalFeatureFlags } from '@/lib/providers/feature-flags-provider';
import {
  EXTERNAL_HANDLING_FEATURE_FLAG,
  useSetInstitutionHandling
} from '@/lib/services/matter-handling';
import type { Handling } from '@/lib/services/matter-handling';
import { HandlingDialog } from './handling-dialog';

/**
 * The set/revert machinery for one institution, shared by the two surfaces that
 * offer it on the institution sheet (VALE-1760).
 *
 * It exists because those surfaces are deliberately in two different places —
 * hand-over lives in the sheet HEADER while the institution is internal, and
 * take-back / edit live in the BODY panel that replaces the composer once it is
 * not. Each is rendered in a different subtree, so neither can host the other, and
 * the dialog, the mutation and the feature gate would otherwise exist twice with
 * nothing keeping the second copy in step.
 *
 * Take-back is one click and never opens the dialog: an undo that costs the same
 * as the original action is the asymmetry EH-005 exists to remove. It also clears
 * the note and the expected-by date server-side, so nothing stale is left behind.
 */
export function useInstitutionHandling({
  matterId,
  institutionId,
  institutionName,
  handling
}: {
  matterId: number;
  institutionId: number;
  institutionName: string;
  handling: Handling | null | undefined;
}) {
  const { hasFeature } = useOptionalFeatureFlags();
  const [dialogOpen, setDialogOpen] = useState(false);
  const setHandling = useSetInstitutionHandling(matterId);

  const enabled = hasFeature(EXTERNAL_HANDLING_FEATURE_FLAG);
  const isHandled = !!handling?.handledBy;

  const takeBack = () =>
    setHandling.mutate({ institutionId, handledByContactMatterId: null });

  /**
   * Rendered by whichever surface offers it. Since VALE-1802 the header menu
   * renders in both states, so on a handed-over institution the menu and the
   * panel are both mounted and each holds its own copy of this dialog and its own
   * `dialogOpen`. That is correct rather than merely tolerable: they are separate
   * hook instances, so opening from one cannot leave the other's state half-set,
   * and the mutation underneath is keyed on the same institution either way.
   */
  const dialog = (
    <HandlingDialog
      matterId={matterId}
      open={dialogOpen}
      onOpenChange={setDialogOpen}
      handling={handling}
      subjectLabel={institutionName}
      isPending={setHandling.isPending}
      onSubmit={(data, handlerName) =>
        setHandling.mutate(
          { institutionId, handlerName, ...data },
          { onSuccess: () => setDialogOpen(false) }
        )
      }
    />
  );

  return {
    enabled,
    isHandled,
    isPending: setHandling.isPending,
    openDialog: () => setDialogOpen(true),
    takeBack,
    dialog
  };
}
